<script setup>
import { router } from '@inertiajs/vue3';
import InputField from '@js/components/fields/InputField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import FormTitle from '@js/components/forms/FormTitle.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { reactive, ref } from 'vue';
import { boolean, object } from 'yup';

const { tenant, externalService, eCommerceStores } = defineProps({
    tenant: {
        required: true,
        type: Object,
    },
    externalService: {
        required: true,
        type: Object,
    },
    eCommerceStores: {
        required: true,
        type: Array,
    },
});

const { route } = useRoute();

const isSubmitting = ref(false);

const form = reactive({
    active: externalService.active,
    accounts: Object.fromEntries(
        eCommerceStores.map((store) => [
            store.cmsStoreId,
            {
                id: externalService.data.accounts?.[store.cmsStoreId]?.id ?? '',
            },
        ]),
    ),
});

const validationSchema = object({
    active: boolean().required('Ce champ est requis.'),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route(
                'front.projectManagement.customer.externalServices.postGoogleMerchant',
                {
                    tenant: tenant.id,
                },
            ),
            {
                active: form.active,
                accounts: form.accounts,
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route(
                    'front.projectManagement.customer.externalServices.googleMerchant',
                    {
                        tenant: tenant.id,
                    },
                ),
            );
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);

            router.visit(
                route('front.projectManagement.customer.externalServices'),
            );
        });
}
</script>

<template>
    <FormRoot
        v-slot="{ errors }"
        :validation-schema="validationSchema"
        :submit="submit"
    >
        <FormColumn>
            <FormContent>
                <SwitchField
                    v-model="form.active"
                    label="Actif"
                    field-name="active"
                    field-id="active"
                />
            </FormContent>

            <FormContent
                v-for="store in eCommerceStores"
                :key="store.cmsStoreId"
            >
                <FormTitle>
                    {{ store.name }}
                </FormTitle>

                <FormGrid>
                    <InputField
                        v-model="form.accounts[store.cmsStoreId].id"
                        label="ID du compte"
                        :field-name="`accounts.${store.cmsStoreId}.id`"
                        :field-id="`accounts.${store.cmsStoreId}.id`"
                    />
                </FormGrid>
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors" />
    </FormRoot>
</template>
